<template>
  <v-dialog v-model="internalOpen" max-width="500">
    <div class="ai-visibility-frequency-dialog">
      <div class="ai-visibility-frequency-dialog__head">
        <h2>Change refresh frequency</h2>
        <button
          type="button"
          class="ai-visibility-frequency-dialog__close"
          aria-label="Close frequency dialog"
          :disabled="saving"
          @click="close"
        >
          <span class="material-icons">close</span>
        </button>
      </div>

      <div class="ai-visibility-frequency-dialog__body">
        <p>How often should this report re-run all tracked questions?</p>

        <div class="ai-visibility-frequency-dialog__cards">
          <label
            v-for="option in frequencyOptions"
            :key="option.value"
            class="ai-visibility-frequency-dialog__card"
            :class="{
              'ai-visibility-frequency-dialog__card--active': draftFrequency === option.value,
            }"
          >
            <input
              v-model="draftFrequency"
              type="radio"
              name="ai-visibility-frequency-choice"
              :value="option.value"
              :disabled="saving"
            >
            <span>
              <span class="ai-visibility-frequency-dialog__card-title">
                {{ option.label }}
              </span>
              <span class="ai-visibility-frequency-dialog__card-subtitle">
                {{ option.subtitle }}
              </span>
            </span>
          </label>
        </div>

        <p class="ai-visibility-frequency-dialog__note">
          Takes effect from your next refresh. Your questions and history are unaffected.
        </p>
      </div>

      <div class="ai-visibility-frequency-dialog__actions">
        <button
          type="button"
          class="ai-visibility-frequency-dialog__cancel"
          :disabled="saving"
          @click="close"
        >
          Cancel
        </button>
        <button
          type="button"
          class="ai-visibility-frequency-dialog__save"
          :disabled="!canConfirm || saving"
          @click="confirm"
        >
          {{ saving ? 'Saving...' : 'Save frequency' }}
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<script>
export default {
  name: 'ChangeTrackerFrequencyDialog',
  props: {
    open: {
      type: Boolean,
      default: false,
    },
    tracker: {
      type: Object,
      default: null,
    },
    saving: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      draftFrequency: 'weekly',
    }
  },
  computed: {
    internalOpen: {
      get() {
        return this.open
      },
      set(value) {
        if (!value) {
          this.close()
        }
      },
    },
    currentFrequency() {
      return this.tracker?.refreshFrequency || 'weekly'
    },
    requiredCredits() {
      return Number(this.tracker?.requiredCredits || 0)
    },
    frequencyOptions() {
      return [
        {
          value: 'weekly',
          label: 'Weekly',
          subtitle: `${this.requiredCredits} POP credits / week · best while you're actively working on visibility`,
        },
        {
          value: 'monthly',
          label: 'Monthly',
          subtitle: `${this.requiredCredits} POP credits / month · lighter on credits, good for steady keywords`,
        },
      ]
    },
    canConfirm() {
      return Boolean(this.tracker) &&
        this.draftFrequency &&
        this.draftFrequency !== this.currentFrequency
    },
  },
  watch: {
    open(value) {
      if (value) {
        this.syncDraftFrequency()
      }
    },
    tracker() {
      if (this.open) {
        this.syncDraftFrequency()
      }
    },
  },
  methods: {
    syncDraftFrequency() {
      this.draftFrequency = this.currentFrequency
    },
    close() {
      if (this.saving) {
        return
      }
      this.$emit('close')
    },
    confirm() {
      if (!this.canConfirm || this.saving) {
        return
      }

      this.$emit('confirm', {
        tracker: this.tracker,
        refreshFrequency: this.draftFrequency,
      })
    },
  },
}
</script>

<style scoped lang="scss">

.ai-visibility-frequency-dialog {
  overflow: hidden;
  border-radius: 16px;
  background: var(--v-cardBg-base);
  box-shadow: 0 30px 80px rgba(1, 35, 35, 0.3);
  color: var(--v-mainColor-base);
}

.ai-visibility-frequency-dialog__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 24px 28px 0;
}

.ai-visibility-frequency-dialog__head h2 {
  flex: 1;
  color: var(--v-mainColor-base);
  font-size: 21px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0;
}

.ai-visibility-frequency-dialog__close {
  display: inline-flex;
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 8px;
  background: transparent;
  color: var(--v-darkGrayColor-base);
  cursor: pointer;
  padding: 0;
}

.ai-visibility-frequency-dialog__close:hover,
.ai-visibility-frequency-dialog__close:focus {
  background: var(--v-gray7Color-base);
  color: var(--v-mainColor-base);
  outline: none;
}

.ai-visibility-frequency-dialog__close:disabled {
  cursor: default;
  opacity: 0.55;
}

.ai-visibility-frequency-dialog__close span {
  font-size: 20px;
}

.ai-visibility-frequency-dialog__body {
  padding: 18px 28px 4px;
}

.ai-visibility-frequency-dialog__body p {
  color: var(--v-darkGrayColor-base);
  font-size: 14px;
  line-height: 1.55;
  margin: 0 0 14px;
}

.ai-visibility-frequency-dialog__cards {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 4px 0 2px;
}

.ai-visibility-frequency-dialog__card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  border: 1.5px solid var(--v-gray13Color-base);
  border-radius: 10px;
  cursor: pointer;
  padding: 13px 15px;
  transition: border-color 0.15s, background 0.15s;
}

.ai-visibility-frequency-dialog__card:hover {
  border-color: var(--v-darkGrayColor-base);
}

.ai-visibility-frequency-dialog__card--active {
  border-color: var(--v-profileAnchorColor-base);
  background: rgba(255, 87, 0, 0.08);
}

.ai-visibility-frequency-dialog__card input {
  flex: 0 0 auto;
  accent-color: var(--v-profileAnchorColor-base);
  margin-top: 3px;
}

.ai-visibility-frequency-dialog__card-title,
.ai-visibility-frequency-dialog__card-subtitle {
  display: block;
}

.ai-visibility-frequency-dialog__card-title {
  color: var(--v-mainColor-base);
  font-size: 14px;
  font-weight: 700;
}

.ai-visibility-frequency-dialog__card-subtitle {
  color: var(--v-darkGrayColor-base);
  font-size: 12.5px;
  line-height: 1.35;
  margin-top: 2px;
}

.ai-visibility-frequency-dialog__body .ai-visibility-frequency-dialog__note {
  color: var(--v-darkGrayColor-base);
  font-size: 12.5px;
  margin: 12px 0 0;
}

.ai-visibility-frequency-dialog__actions {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 18px 28px 24px;
}

.ai-visibility-frequency-dialog__cancel,
.ai-visibility-frequency-dialog__save {
  border-radius: 999px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  min-height: 38px;
  padding: 0 18px;
}

.ai-visibility-frequency-dialog__cancel {
  border: 0;
  background: transparent;
  color: var(--v-darkGrayColor-base);
}

.ai-visibility-frequency-dialog__cancel:hover,
.ai-visibility-frequency-dialog__cancel:focus {
  color: var(--v-mainColor-base);
  outline: none;
}

.ai-visibility-frequency-dialog__save {
  margin-left: auto;
  border: 1px solid var(--v-profileAnchorColor-base);
  background: var(--v-profileAnchorColor-base);
  color: var(--v-whiteColor-base);
}

.ai-visibility-frequency-dialog__save:hover,
.ai-visibility-frequency-dialog__save:focus {
  background: var(--v-anchor1Color-base);
  outline: none;
}

.ai-visibility-frequency-dialog__cancel:disabled,
.ai-visibility-frequency-dialog__save:disabled {
  cursor: default;
  opacity: 0.55;
}

@media (max-width: 600px) {
  .ai-visibility-frequency-dialog__head {
    padding: 22px 22px 0;
  }

  .ai-visibility-frequency-dialog__body {
    padding: 18px 22px 4px;
  }

  .ai-visibility-frequency-dialog__actions {
    padding: 18px 22px 22px;
  }
}

</style>
